import React, { useEffect, useState, useMemo } from "react";
import { Row, Col, Card, CardBody, CardTitle, Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Spinner, Alert } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { pb } from "../../helpers/pocketbase_helper";
import Switch from "react-switch";
import TableContainer from "../../components/Common/DataTableContainer";

const TiposSolicitud = () => {
    document.title = "Tipos de Solicitud | GraphicForce - Printesco";

    const [tiposSolicitud, setTiposSolicitud] = useState([]);
    const [tiposSolicitudInactivos, setTiposSolicitudInactivos] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [modalAdd, setModalAdd] = useState(false);
    
    // Estado de paginación
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [isPagination] = useState(true);
    const [isCustomPageSize] = useState(true);

    const [form, setForm] = useState({ nombre: "", campo_orden: false });
    const [saving, setSaving] = useState(false);
    const [formActivo, setFormActivo] = useState(true);

    // For delete confirmation
    const [showDeleteAlert, setShowDeleteAlert] = useState(false);
    const [tipoSolicitudToDelete, setTipoSolicitudToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [modalError, setModalError] = useState("");
    
    // For restore confirmation
    const [showRestoreAlert, setShowRestoreAlert] = useState(false);
    const [tipoSolicitudToRestore, setTipoSolicitudToRestore] = useState(null);
    const [restoring, setRestoring] = useState(false);

    // Fetch todos los tipos de solicitud y separarlos por estado
    const fetchTiposSolicitud = async () => {
        try {
            // Obtener todos los tipos de solicitud de una vez
            const resultList = await pb.collection("tipo_solicitud").getList(1, 1000, {
                sort: "-created"
            });
            
            // Separar tipos activos e inactivos
            const activos = resultList.items.filter(tipo => tipo.activo !== false);
            const inactivos = resultList.items.filter(tipo => tipo.activo === false);
            
            // Aplicar paginación solo a los activos
            const startIndex = pageIndex * pageSize;
            const endIndex = startIndex + pageSize;
            const activosPaginados = activos.slice(startIndex, endIndex);
            
            setTiposSolicitud(activosPaginados);
            setTiposSolicitudInactivos(inactivos);
            setTotalRecords(activos.length);
        } catch (err) {
            console.error("Error fetching tipos de solicitud:", err);
        }
    };

    useEffect(() => {
        fetchTiposSolicitud();
    }, [pageIndex, pageSize]);

    const reloadTiposSolicitud = () => {
        fetchTiposSolicitud();
    };

    // Handlers
    const handleAdd = () => {
        setForm({ nombre: "", campo_orden: false });
        setFormActivo(true);
        setModalError("");
        setModalAdd(true);
    };



    const handleRemove = (tipoSolicitud) => {
        setTipoSolicitudToDelete(tipoSolicitud);
        setShowDeleteAlert(true);
        // Hacer scroll hacia arriba para mostrar el alert
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
    };

    const confirmRemove = async () => {
        if (!tipoSolicitudToDelete) return;
        setDeleting(true);
        try {
            await pb.collection("tipo_solicitud").update(tipoSolicitudToDelete.id, { activo: false });
            fetchTiposSolicitud();
            setShowDeleteAlert(false);
            setTipoSolicitudToDelete(null);
            setModalError("");
        } catch (err) {
            setModalError("Error al desactivar tipo de solicitud");
        }
        setDeleting(false);
    };

    const cancelRemove = () => {
        setShowDeleteAlert(false);
        setTipoSolicitudToDelete(null);
    };

    // Show alert and store tipo de solicitud to restore
    const handleRestore = (tipoSolicitud) => {
        setTipoSolicitudToRestore(tipoSolicitud);
        setShowRestoreAlert(true);
        // Hacer scroll hacia arriba para mostrar el alert
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
    };

    // Confirm restore: set activo to true
    const confirmRestore = async () => {
        if (!tipoSolicitudToRestore) return;
        setRestoring(true);
        try {
            await pb.collection("tipo_solicitud").update(tipoSolicitudToRestore.id, { activo: true });
            fetchTiposSolicitud();
            setShowRestoreAlert(false);
            setTipoSolicitudToRestore(null);
            setModalError("");
        } catch (err) {
            setModalError("Error al restaurar tipo de solicitud");
        }
        setRestoring(false);
    };

    // Cancel restore
    const cancelRestore = () => {
        setShowRestoreAlert(false);
        setTipoSolicitudToRestore(null);
    };

    const handleChange = e => {
        const { name, type, checked, value } = e.target;
        const next = type === 'checkbox' ? checked : (name === "nombre" ? value.toUpperCase() : value);
        setForm({ ...form, [name]: next });
    };

    const handleSave = async e => {
        e.preventDefault();
        setSaving(true);
        setModalError("");
        try {
            const data = {
                nombre: form.nombre,
                campo_orden: !!form.campo_orden,
                activo: true,
            };
            await pb.collection("tipo_solicitud").create(data);
            fetchTiposSolicitud();
            setModalAdd(false);
        } catch (err) {
            // Manejo de error de unicidad
            if (
                err?.data?.data?.nombre &&
                err.data.data.nombre.code === "validation_not_unique"
            ) {
                setModalError(
                    `El tipo de solicitud con el nombre "${form.nombre}" ya existe, por favor valide la información e intente de nuevo`
                );
            } else {
                setModalError("Error al agregar tipo de solicitud");
            }
        }
        setSaving(false);
    };



    // --- Filtering and Pagination ---
    const columns = useMemo(() => [
        {
            header: "Nombre",
            accessorKey: "nombre",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Activo",
            accessorKey: "activo",
            cell: info =>
                info.getValue() === false
                    ? <span className="badge bg-danger">No</span>
                    : <span className="badge bg-success">Sí</span>,
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Usa Orden",
            accessorKey: "campo_orden",
            cell: info =>
                info.getValue() ? <span className="badge bg-primary">Sí</span> : <span className="badge bg-secondary">No</span>,
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Acciones",
            accessorKey: "acciones",
            cell: info => {
                const tipoSolicitud = info.row.original;
                return (
                    <div style={{ display: "flex", gap: 4, minWidth: 140, maxWidth: 180 }}>
                        <Button color="danger" size="sm" onClick={() => handleRemove(tipoSolicitud)} disabled={tipoSolicitud.activo === false}>
                            <i className="fas fa-eraser me-1"></i> Eliminar
                        </Button>
                    </div>
                );
            },
            enableColumnFilter: false,
            enableSorting: false,
            meta: { style: { width: "1%", whiteSpace: "nowrap" } },
        },
    ], [tiposSolicitud]);

    // Columnas para tipos de solicitud inactivos
    const columnsInactivos = useMemo(() => [
        {
            header: "Nombre",
            accessorKey: "nombre",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Estado",
            accessorKey: "activo",
            cell: info =>
                <span className="badge bg-danger">Inactivo</span>,
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Acciones",
            accessorKey: "acciones",
            cell: info => {
                const tipoSolicitud = info.row.original;
                return (
                    <div style={{ display: "flex", gap: 4, minWidth: 140, maxWidth: 180 }}>
                        <Button color="success" size="sm" onClick={() => handleRestore(tipoSolicitud)}>
                            <i className="fas fa-undo me-1"></i> Restaurar
                        </Button>
                    </div>
                );
            },
            enableColumnFilter: false,
            enableSorting: false,
            meta: { style: { width: "1%", whiteSpace: "nowrap" } },
        },
    ], [tiposSolicitudInactivos]);

    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    <Breadcrumbs maintitle="Dashboard" title="Administracion" breadcrumbItem="Tipos de Solicitud" />

                    {/* Dismissing Alert for Delete */}
                    {showDeleteAlert && tipoSolicitudToDelete && (
                        <Alert
                            color="warning"
                            isOpen={showDeleteAlert}
                            toggle={cancelRemove}
                            fade={true}
                            transition={{ timeout: 150 }}
                            className="d-flex align-items-center justify-content-between"
                        >
                            <div>
                                <strong>¿Está seguro?</strong> Esto desactivará el tipo de solicitud <b>{tipoSolicitudToDelete.nombre}</b>.
                            </div>
                            <div>
                                <Button color="danger" size="sm" className="me-2" onClick={confirmRemove} disabled={deleting}>
                                    {deleting ? <Spinner size="sm" /> : <i className="fas fa-eraser me-1"></i>} Confirmar
                                </Button>
                                <Button color="secondary" size="sm" onClick={cancelRemove} disabled={deleting}>
                                    Cancelar
                                </Button>
                            </div>
                        </Alert>
                    )}

                    {/* Dismissing Alert for Restore */}
                    {showRestoreAlert && tipoSolicitudToRestore && (
                        <Alert
                            color="info"
                            isOpen={showRestoreAlert}
                            toggle={cancelRestore}
                            fade={true}
                            transition={{ timeout: 150 }}
                            className="d-flex align-items-center justify-content-between"
                        >
                            <div>
                                <strong>¿Está seguro?</strong> Esto restaurará el tipo de solicitud <b>{tipoSolicitudToRestore.nombre}</b>.
                            </div>
                            <div>
                                <Button color="success" size="sm" className="me-2" onClick={confirmRestore} disabled={restoring}>
                                    {restoring ? <Spinner size="sm" /> : <i className="fas fa-undo me-1"></i>} Confirmar
                                </Button>
                                <Button color="secondary" size="sm" onClick={cancelRestore} disabled={restoring}>
                                    Cancelar
                                </Button>
                            </div>
                        </Alert>
                    )}

                    <Row>
                        <Col>
                            <Card>
                                <CardBody>
                                    <CardTitle className="h4 d-flex justify-content-between align-items-center">
                                        Tipos de Solicitud
                                        <div>
                                            <Button color="primary" size="sm" onClick={handleAdd}>
                                                <i className="fas fa-plus me-1"></i> Agregar
                                            </Button>
                                            <Button
                                                color="secondary"
                                                size="sm"
                                                className="ms-2"
                                                title="Recargar tabla"
                                                onClick={reloadTiposSolicitud}
                                            >
                                                <i className="fas fa-sync-alt"></i>
                                            </Button>
                                        </div>
                                    </CardTitle>
                                    <div className="table-rep-plugin">
                                        <div className="table-responsive mb-0" data-pattern="priority-columns">
                                            <TableContainer
                                                columns={columns}
                                                data={tiposSolicitud}
                                                isGlobalFilter={false}
                                                isPagination={true}
                                                isCustomPageSize={true}
                                                pageIndex={pageIndex}
                                                setPageIndex={setPageIndex}
                                                pageSize={pageSize}
                                                setPageSize={setPageSize}
                                                totalRecords={totalRecords}
                                                pagination="pagination"
                                                paginationWrapper='dataTables_paginate paging_simple_numbers'
                                                tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                                            />
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    {/* Tabla de Tipos de Solicitud Inactivos */}
                    <Row className="mt-4">
                        <Col>
                            <Card>
                                <CardTitle className="h4 d-flex justify-content-between align-items-center">
                                    Tipos de Solicitud Inactivos
                                    <div>
                                        <Button
                                            color="secondary"
                                            size="sm"
                                            title="Recargar tabla"
                                            onClick={reloadTiposSolicitud}
                                        >
                                            <i className="fas fa-sync-alt"></i>
                                        </Button>
                                    </div>
                                </CardTitle>
                                <div className="table-rep-plugin">
                                    <div className="table-responsive mb-0" data-pattern="priority-columns">
                                        <TableContainer
                                            columns={columnsInactivos}
                                            data={tiposSolicitudInactivos}
                                            isGlobalFilter={false}
                                            isPagination={false}
                                            isCustomPageSize={false}
                                            tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                                        />
                                    </div>
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Modal Agregar */}
            <Modal isOpen={modalAdd} toggle={() => setModalAdd(!modalAdd)} size="lg">
                <ModalHeader toggle={() => setModalAdd(!modalAdd)}>Agregar Tipo de Solicitud</ModalHeader>
                <Form onSubmit={handleSave}>
                    <ModalBody>
                        {modalError && (
                            <Alert
                                color="danger"
                                isOpen={!!modalError}
                                toggle={() => setModalError("")}
                                fade={true}
                                transition={{ timeout: 150 }}
                                className="d-flex align-items-center justify-content-between mb-3"
                            >
                                <div>
                                    <strong>Error:</strong> {modalError}
                                </div>
                            </Alert>
                        )}
                        <FormGroup>
                            <Label for="nombre">Nombre</Label>
                            <Input
                                type="text"
                                name="nombre"
                                id="nombre"
                                value={form.nombre}
                                onChange={handleChange}
                                required
                            />
                        </FormGroup>
                        <FormGroup className="mt-2">
                            <Label className="me-2">Usa Orden de Producción</Label>
                            <Input
                                type="checkbox"
                                name="campo_orden"
                                checked={!!form.campo_orden}
                                onChange={handleChange}
                            />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setModalAdd(false)}>
                            Cancelar
                        </Button>
                        <Button color="primary" type="submit" disabled={saving}>
                            {saving ? <Spinner size="sm" className="me-2" /> : null}
                            {saving ? "Guardando..." : "Guardar"}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>


        </React.Fragment>
    );
};

export default TiposSolicitud;


